import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Activity, AlertTriangle, Link2, CheckCircle2, XCircle, Loader2, RefreshCw, Trash2, Plus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const GRADE_COLOR = { A: 'text-green-600', B: 'text-blue-600', C: 'text-amber-600', D: 'text-red-600' };
const BL_STATUS = {
  active: 'bg-green-100 text-green-700', lost: 'bg-red-100 text-red-700',
  pending: 'bg-amber-100 text-amber-700', error: 'bg-slate-200 text-slate-600',
};

export default function AdminSeoHealth({ getAuthHeaders }) {
  const [health, setHealth] = useState(null);
  const [soft404, setSoft404] = useState(null);
  const [backlinks, setBacklinks] = useState({ backlinks: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [src, setSrc] = useState('');
  const [anchor, setAnchor] = useState('');
  const [checkingId, setCheckingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, b] = await Promise.all([
        fetch(`${API}/api/admin/seo/health`, { headers: getAuthHeaders() }).then(r => r.json()),
        fetch(`${API}/api/admin/seo/backlinks`, { headers: getAuthHeaders() }).then(r => r.json()),
      ]);
      setHealth(h); setBacklinks(b);
    } catch (e) { /* noop */ }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const scanSoft404 = async () => {
    setScanning(true);
    try {
      const r = await fetch(`${API}/api/admin/seo/soft-404s?limit=100`, { headers: getAuthHeaders() });
      setSoft404(await r.json());
    } catch (e) { /* noop */ }
    setScanning(false);
  };

  const addBacklink = async (e) => {
    e.preventDefault();
    if (!src.trim()) return;
    await fetch(`${API}/api/admin/seo/backlinks`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ source_url: src.trim(), anchor: anchor.trim() }),
    });
    setSrc(''); setAnchor(''); load();
  };

  const checkBacklink = async (id) => {
    setCheckingId(id);
    await fetch(`${API}/api/admin/seo/backlinks/${id}/check`, { method: 'POST', headers: getAuthHeaders() });
    setCheckingId(null); load();
  };

  const delBacklink = async (id) => {
    await fetch(`${API}/api/admin/seo/backlinks/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    load();
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-seo-health">
      {/* Health score */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-4 mb-4">
            <Activity className="h-6 w-6 text-cyan-600" />
            <div className="flex-1">
              <h2 className="text-lg font-bold text-slate-900">SEO Health</h2>
              <p className="text-xs text-slate-500">Overall site SEO posture across geo pages, content & backlinks</p>
            </div>
            <div className="text-right">
              <div className={`text-4xl font-black ${GRADE_COLOR[health?.grade] || 'text-slate-700'}`} data-testid="seo-health-score">{health?.score ?? 0}</div>
              <div className="text-xs text-slate-400">Grade {health?.grade}</div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {(health?.checks || []).map((c, i) => (
              <div key={i} className="flex items-start gap-2 text-sm p-2 rounded bg-slate-50" data-testid={`seo-check-${i}`}>
                {c.ok ? <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" /> : <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />}
                <div><span className="font-medium text-slate-800">{c.name}</span><div className="text-xs text-slate-500">{c.detail}</div></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Soft 404 monitor */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /><h3 className="font-semibold text-slate-900">Soft 404 / Thin Content Monitor</h3></div>
            <Button size="sm" variant="outline" onClick={scanSoft404} disabled={scanning} data-testid="soft404-scan-btn">
              {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Scan
            </Button>
          </div>
          {soft404 ? (
            <>
              <p className="text-sm text-slate-600 mb-2">
                Scanned <strong>{soft404.total_scanned}</strong> pages — <strong className="text-green-600">{soft404.healthy_count}</strong> healthy,
                <strong className="text-red-600"> {soft404.flagged_count}</strong> flagged.
              </p>
              {soft404.flagged_count > 0 && (
                <div className="space-y-1 max-h-64 overflow-auto">
                  {soft404.flagged.map((f, i) => (
                    <div key={i} className="text-xs border border-amber-100 rounded p-2" data-testid={`soft404-item-${i}`}>
                      <a href={f.url} target="_blank" rel="noreferrer" className="text-blue-600 break-all">{f.url}</a>
                      <div className="text-slate-500 mt-0.5">EN: {f.en_issues.join(', ') || 'ok'} {f.es_issues.length ? `· ES: ${f.es_issues.join(', ')}` : ''}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : <p className="text-sm text-slate-400">Click "Scan" to check published geo pages for thin/broken content.</p>}
        </CardContent>
      </Card>

      {/* Backlink tracker */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3"><Link2 className="h-5 w-5 text-indigo-600" /><h3 className="font-semibold text-slate-900">Backlink Tracker</h3>
            <div className="ml-auto flex gap-2 text-xs">
              <Badge className="bg-green-100 text-green-700">Active {backlinks.stats?.active || 0}</Badge>
              <Badge className="bg-red-100 text-red-700">Lost {backlinks.stats?.lost || 0}</Badge>
            </div>
          </div>
          <form onSubmit={addBacklink} className="flex flex-wrap gap-2 mb-3">
            <Input value={src} onChange={(e) => setSrc(e.target.value)} placeholder="https://referring-site.com/page" className="flex-1 min-w-[220px]" data-testid="backlink-source-input" />
            <Input value={anchor} onChange={(e) => setAnchor(e.target.value)} placeholder="Anchor text (optional)" className="w-44" data-testid="backlink-anchor-input" />
            <Button type="submit" disabled={!src.trim()} data-testid="backlink-add-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
          </form>
          {(backlinks.backlinks || []).length === 0 ? (
            <p className="text-sm text-slate-400">No backlinks tracked yet. Add referring URLs to monitor whether they still link to you.</p>
          ) : (
            <div className="space-y-2">
              {backlinks.backlinks.map((b) => (
                <div key={b.id} className="flex items-center gap-2 text-sm border border-slate-100 rounded p-2" data-testid={`backlink-${b.id}`}>
                  <Badge className={`text-[10px] ${BL_STATUS[b.status] || ''}`}>{b.status}{b.http_status ? ` ${b.http_status}` : ''}</Badge>
                  <a href={b.source_url} target="_blank" rel="noreferrer" className="text-blue-600 truncate flex-1">{b.source_url}</a>
                  {b.last_checked && <span className="text-[10px] text-slate-400">{b.last_checked.slice(0, 10)}</span>}
                  <Button size="sm" variant="ghost" onClick={() => checkBacklink(b.id)} disabled={checkingId === b.id} data-testid={`backlink-check-${b.id}`}>
                    {checkingId === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => delBacklink(b.id)} className="text-red-400 hover:text-red-600" data-testid={`backlink-del-${b.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
